import React from 'react';
import { useNavigate } from 'react-router-dom';
import RoleCard from './components/RoleCard';
import StatsSection from './components/StatsSection';
import TestimonialsSection from './components/TestimonialsSection';
import LandingFooter from './components/LandingFooter';

const roles = [
  {
    id: 'student',
    title: 'Student',
    icon: '🎓',
    gradient: 'linear-gradient(135deg, #2D5A87 0%, #4A90E2 100%)',
    accentColor: '#2D5A87',
    shadowColor: 'rgba(45, 90, 135, 0.4)',
    description: 'Discover opportunities, apply to jobs and internships, and accelerate your career with AI-powered matching.',
    features: [
      'AI-powered job matching',
      'One-click applications',
      'Skill gap analysis',
      'Interview scheduling',
    ],
    path: '/job-search-application',
  },
  {
    id: 'employer',
    title: 'Employer',
    icon: '🏢',
    gradient: 'linear-gradient(135deg, #8B4513 0%, #D4813A 100%)',
    accentColor: '#8B4513',
    shadowColor: 'rgba(139, 69, 19, 0.4)',
    description: 'Post jobs, manage candidates, and hire top talent efficiently with smart screening tools.',
    features: [
      'Smart candidate screening',
      'Job & internship posting',
      'Candidate management',
      'Analytics & reporting',
    ],
    path: '/job-posting',
  },
  {
    id: 'institution',
    title: 'Institution',
    icon: '🏛️',
    gradient: 'linear-gradient(135deg, #27AE60 0%, #52D68A 100%)',
    accentColor: '#27AE60',
    shadowColor: 'rgba(39, 174, 96, 0.4)',
    description: 'Manage student placements, track program outcomes, and build employer relationships.',
    features: [
      'Placement tracking',
      'Program management',
      'Employer relations',
      'Placement analytics',
    ],
    path: '/institution-management-portal',
  },
  {
    id: 'admin',
    title: 'Admin',
    icon: '⚙️',
    gradient: 'linear-gradient(135deg, #6C3483 0%, #A569BD 100%)',
    accentColor: '#6C3483',
    shadowColor: 'rgba(108, 52, 131, 0.4)',
    description: 'Oversee the entire platform, manage registrations, approvals, and system-wide analytics.',
    features: [
      'Registration approvals',
      'System oversight',
      'Predictive analytics',
      'User management',
    ],
    path: '/registration-management-approval-center',
  },
];

const LandingPageRoleSelection = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-[var(--color-border)] sticky top-0 z-50 shadow-warm-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
              style={{ background: 'linear-gradient(135deg, #2D5A87 0%, #E67E22 100%)' }}
            >
              JF
            </div>
            <div>
              <span className="text-xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Crimson Text, serif' }}>
                JOB_Fusion
              </span>
              <p className="text-xs text-[var(--color-muted-foreground)] leading-none">Career Platform</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-[var(--color-muted-foreground)]">
            <a href="#roles" className="hover:text-[var(--color-primary)] transition-colors">Roles</a>
            <a href="#stats" className="hover:text-[var(--color-primary)] transition-colors">About</a>
            <a href="#testimonials" className="hover:text-[var(--color-primary)] transition-colors">Testimonials</a>
            <button
              onClick={() => navigate('/role-based-dashboard')}
              className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-200 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #2D5A87 0%, #E67E22 100%)' }}
            >
              Dashboard
            </button>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, #2D5A87 0%, transparent 50%), radial-gradient(circle at 80% 20%, #E67E22 0%, transparent 50%)',
          }}
        />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2 text-sm text-[var(--color-primary)] font-medium mb-6">
            <span>🚀</span>
            <span>India's #1 Career Fusion Platform</span>
          </div>
          <h1
            className="text-4xl md:text-6xl font-bold text-[var(--color-foreground)] mb-6 leading-tight"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Your Career Journey{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #2D5A87, #E67E22)' }}
            >
              Starts Here
            </span>
          </h1>
          <p className="text-lg md:text-xl text-[var(--color-muted-foreground)] max-w-2xl mx-auto mb-10 leading-relaxed">
            JOB_Fusion connects students, employers, and institutions on a single intelligent platform.
            Select your role to get started.
          </p>
          <a
            href="#roles"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-semibold text-lg transition-all duration-300 hover:opacity-90 hover:-translate-y-1"
            style={{
              background: 'linear-gradient(135deg, #2D5A87 0%, #E67E22 100%)',
              boxShadow: '0 8px 24px rgba(45, 90, 135, 0.35)',
            }}
          >
            Choose Your Role
            <span className="text-xl">↓</span>
          </a>
        </div>
      </section>
      {/* Role Selection Section */}
      <section id="roles" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              Select Your Role
            </h2>
            <p className="text-[var(--color-muted-foreground)] max-w-xl mx-auto">
              Each role provides a tailored experience with features designed specifically for your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {roles?.map((role) => (
              <RoleCard key={role?.id} role={role} />
            ))}
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <div id="stats">
        <StatsSection />
      </div>
      {/* Testimonials */}
      <div id="testimonials">
        <TestimonialsSection />
      </div>
      {/* Footer */}
      <LandingFooter />
    </div>
  );
};

export default LandingPageRoleSelection;
